import { z } from "zod";

export enum UserRole {
  USER = "user",
  RECRUITER = "recruiter",
  SUPER_ADMIN = "super_admin"
}

export enum JobStatus {
  DRAFT = "draft",
  ACTIVE = "active",
  PAUSED = "paused",
  CLOSED = "closed",
  FILLED = "filled"
}

export enum ReferralStatus {
  PENDING = "pending",
  SUBMITTED = "submitted",
  UNDER_REVIEW = "under_review",
  SHORTLISTED = "shortlisted",
  INTERVIEWED = "interviewed",
  HIRED = "hired",
  PASSED_PROBATION = "passed_probation",
  REJECTED = "rejected"
}

// Reward status enum for referrals
export enum RewardStatus {
  PENDING = "pending",
  ELIGIBLE = "eligible", 
  PAID = "paid",
  FORFEITED = "forfeited"
}

// Remote work type enum
export enum RemoteType {
  ONSITE = "onsite",
  REMOTE = "remote",
  HYBRID = "hybrid"
}

// Recommendation status enum for match analysis
export enum RecommendationStatus {
  HIGHLY_RECOMMENDED = "highly_recommended",
  RECOMMENDED = "recommended", 
  CONSIDER = "consider",
  NOT_RECOMMENDED = "not_recommended"
}

// Recruitment urgency status enum
export enum RecruitmentStatus {
  OPEN = "open",
  LIMITED = "limited",
  INTERVIEWING = "interviewing"
}

// Firestore document schemas
export const userSchema = z.object({
  id: z.string(), // Document ID is now the Firebase UID
  email: z.string().email(),
  username: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  role: z.nativeEnum(UserRole),
  profileImageUrl: z.string().optional(),
  passwordResetRequired: z.boolean().optional().default(true),
  onboardingCompleted: z.boolean().optional().default(false),
  invitedAt: z.date().optional(),
  lastPasswordReset: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Candidate resume object schema
export const resumeSchema = z.object({
  url: z.string(),
  fileName: z.string(),
  uploadedAt: z.date(),
  uploadedBy: z.string(), // userId of referrer who uploaded
});

// Anonymized profile schema for processing
export const anonymizedProfileSchema = z.object({
  text: z.string(),
  spannerUserId: z.string(), // UUID v4
  processedAt: z.date(),
});

// Candidates collection schema - Document ID is lowercase email
export const candidateSchema = z.object({
  id: z.string(), // Document ID is the lowercase email
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  linkedinUrl: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  resumeUrl: z.string().optional().nullable(), // Single resume URL instead of array
  resumeFileName: z.string().optional().nullable(), // Store filename for display
  resumes: z.array(resumeSchema).default([]).optional(), // Array of resumes for multiple uploads
  anonymizedProfiles: z.array(anonymizedProfileSchema).default([]).optional(),
  spannerUserId: z.string().optional().nullable(), // UUID v4 for GraphRAG matching
  experienceYears: z.number().optional().nullable(),
  referralCount: z.number().default(0),
  firstReferredBy: z.string(), // userId of first referrer
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Match analysis result schema
export const matchAnalysisResultSchema = z.object({
  jobId: z.string(),
  matchScore: z.number(),
  recommendation: z.string(),
  skillsAnalysis: z.any(),
  experienceAnalysis: z.any(),
  overallAnalysis: z.string(),
  analyzedAt: z.date(),
});

// Match error schema
export const matchErrorSchema = z.object({
  jobId: z.string(),
  error: z.string(),
  failedAt: z.date(),
});

// Admin candidate schema with extended fields for admin operations
export const adminCandidateSchema = z.object({
  id: z.string(), // Document ID is the lowercase email
  // Core candidate information
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  linkedinUrl: z.string().optional(),
  phoneNumber: z.string().optional(),
  
  // Document management
  resumes: z.array(resumeSchema).default([]),
  anonymizedProfiles: z.array(anonymizedProfileSchema).default([]).optional(),
  
  // Basic profile data
  skills: z.array(z.string()).default([]),
  experienceYears: z.number().optional(),
  
  // Referral tracking
  referralCount: z.number().default(0),
  firstReferredBy: z.string(), // userId of first referrer
  
  // System integration
  spannerUserId: z.string().optional(), // UUID for Spanner integration
  embeddingCreatedAt: z.date().optional(),
  
  // Match processing
  matchMakingPending: z.boolean().optional(),
  matchForJobId: z.string().optional(),
  lastMatchAnalysis: matchAnalysisResultSchema.optional(),
  lastMatchError: matchErrorSchema.optional(),
  
  // Timestamps
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const companySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  website: z.string().optional(),
  logoUrl: z.string().optional(),
  industry: z.string().optional(),
  size: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export enum JobPriority {
  LOW = "low",
  NORMAL = "normal", 
  HIGH = "high",
  URGENT = "urgent"
}

export const recruiterSchema = z.object({
  id: z.string(),
  name: z.string(),
});

// Jobs schema
export const jobSchema = z.object({
  id: z.string(),
  title: z.string(),
  internalJobId: z.string(), // company's internal reference
  employmentType: z.string(), // full-time, part-time, contract, internship
  category: z.string(), // engineering, design, sales, marketing, etc.
  location: z.string(),
  remoteType: z.nativeEnum(RemoteType),
  salary: z.string().optional(), // e.g., "$120,000 - $150,000" or "Competitive"
  rewardInAirPoints: z.number(), // required referral bonus
  description: z.string(), // rich text/markdown
  skills: z.array(z.string()).default([]), // required skills for matching
  status: z.nativeEnum(JobStatus).default(JobStatus.DRAFT),
  priority: z.nativeEnum(JobPriority).default(JobPriority.NORMAL),
  recruitmentStatus: z.nativeEnum(RecruitmentStatus).default(RecruitmentStatus.OPEN).optional(),
  department: z.string().optional(),
  recruiterId: z.string(), // references users collection
  assignedRecruiter: recruiterSchema.optional(), // embedded recruiter info
  targetHires: z.number().default(1),
  currentHires: z.number().default(0),
  referralCount: z.number().default(0),
  viewCount: z.number().default(0),
  // Analytics and metrics fields
  applicants: z.number().default(0), // total applications including non-referrals
  totalEarnings: z.number().default(0), // total AirPoints paid out for this job
  avgMatchScore: z.number().default(0), // average matching score of referrals
  conversionRate: z.number().default(0), // percentage of referrals that get hired
  daysToFill: z.number().optional(), // estimated or actual days to fill position
  lastActivityAt: z.date().optional(), // last referral or application activity
  importantNotes: z.string().optional(), // Role requirements for referrers
  expiresAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  publishedAt: z.date().optional(), // when status changed to active
});

// Job Settings singleton document schema
export const jobSettingsSchema = z.object({
  id: z.literal("global"), // Fixed string "global" (singleton document)
  employmentTypes: z.array(z.string()),
  categories: z.array(z.string()),
  skills: z.array(z.string()), // master list
  locations: z.array(z.string()), // common locations
  priorities: z.array(z.string()), // priority levels
  defaultRewardAmount: z.number(),
  minRewardAmount: z.number(),
  maxRewardAmount: z.number(),
  referralExpirationDays: z.number().default(90),
  lastUpdated: z.date(),
  updatedBy: z.string(), // admin userId
});

// Status history entry schema
export const statusHistorySchema = z.object({
  status: z.string(),
  changedAt: z.date(),
  changedBy: z.string(), // userId
  notes: z.string().nullable().optional(),
});

// Recruiter note schema
export const recruiterNoteSchema = z.object({
  note: z.string(),
  date: z.date(), // Firebase timestamp
  createdBy: z.string().nullable().optional(), // userId of the recruiter who added the note
});

// Updated referrals schema - references candidates by email
export const referralSchema = z.object({
  id: z.string(),
  jobId: z.string(),
  referrerId: z.string(),
  candidateEmail: z.string().email(), // Reference to candidates collection
  referralNote: z.string().nullable().optional(),
  relationship: z.string().nullable().optional(),
  status: z.nativeEnum(ReferralStatus).default(ReferralStatus.PENDING),
  statusHistory: z.array(statusHistorySchema).default([]),
  rewardStatus: z.nativeEnum(RewardStatus).default(RewardStatus.PENDING),
  rewardAmount: z.number().optional(), // in AirPoints
  reviewedAt: z.date().optional(),
  reviewedBy: z.string().nullable().optional(),
  reviewNotes: z.string().nullable().optional(),
  interviewScheduledAt: z.date().optional(),
  hiredAt: z.date().optional(),
  // Rejection feedback
  rejectionNotes: z.string().optional(),
  rejectedAt: z.date().optional(),
  rejectedBy: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Skills analysis schema for AI matching
export const skillsAnalysisSchema = z.object({
  matchedSkills: z.array(z.string()).default([]),
  missingSkills: z.array(z.string()).default([]),
  additionalSkills: z.array(z.string()).default([]).optional(),
  skillMatchPercentage: z.number().optional(),
}).optional();

// Experience analysis schema for AI matching
export const experienceAnalysisSchema = z.object({
  yearsOfExperience: z.number().optional(),
  requiredExperience: z.number().optional(),
  experienceMatchLevel: z.enum(['exceeds', 'meets', 'below']).optional(),
  experienceAssessment: z.string().optional(),
}).optional();

// Admin referral schema - matches actual Firestore structure
export const adminReferralSchema = z.object({
  id: z.string(),
  
  // Core referral fields
  jobId: z.string(),
  referrerId: z.string(),
  candidateEmail: z.string().email(),
  referralNote: z.string().nullable().optional(),
  relationship: z.string().nullable().optional(),
  
  // Status management
  status: z.nativeEnum(ReferralStatus).default(ReferralStatus.PENDING),
  statusHistory: z.array(statusHistorySchema).default([]),
  
  // Reward management
  rewardStatus: z.nativeEnum(RewardStatus).default(RewardStatus.PENDING),
  rewardAmount: z.number().optional(),
  
  // Recruiter notes
  recruiterNote: z.array(recruiterNoteSchema).default([]),
  
  // Hiring
  hiredAt: z.date().optional(),
  
  // AI Match analysis results (generated by AI matching agent)
  matchScore: z.number().optional(), // 0-1 decimal or 0-100 percentage
  recommendation: z.nativeEnum(RecommendationStatus).optional(),
  overallAnalysis: z.string().nullable().optional(), // Markdown-formatted AI analysis
  matchAnalyzedAt: z.date().optional(),
  skillsAnalysis: skillsAnalysisSchema, // Skills gap analysis
  experienceAnalysis: experienceAnalysisSchema, // Experience analysis
  
  // Special features
  bonusPoints: z.boolean().optional(),
  requiresResumeUpload: z.boolean().optional(),
  
  // Rejection feedback
  rejectionNotes: z.string().optional(),
  rejectedAt: z.date().optional(),
  rejectedBy: z.string().optional(),
  
  // Timestamps
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Insert schemas (without id, createdAt, updatedAt)
export const insertUserSchema = userSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCandidateSchema = candidateSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  referralCount: true, // Auto-calculated
});

export const insertCompanySchema = companySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertJobSchema = jobSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  currentHires: true, // Auto-calculated
  referralCount: true, // Auto-calculated
  viewCount: true, // Auto-calculated
  applicants: true, // Auto-calculated
  totalEarnings: true, // Auto-calculated
  avgMatchScore: true, // Auto-calculated
  conversionRate: true, // Auto-calculated
  lastActivityAt: true, // Auto-calculated
  publishedAt: true, // Set when status changes to active
});

export const insertReferralSchema = referralSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  statusHistory: true, // Auto-generated
  reviewedAt: true,
  reviewedBy: true,
  reviewNotes: true,
  interviewScheduledAt: true,
  hiredAt: true,
});

export const insertAdminReferralSchema = adminReferralSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  statusHistory: true, // Auto-generated
  hiredAt: true,
  matchAnalyzedAt: true, // Auto-generated
});

export const insertAdminCandidateSchema = adminCandidateSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  referralCount: true, // Auto-calculated
  embeddingCreatedAt: true, // Auto-generated
  lastMatchAnalysis: true, // Auto-generated
  lastMatchError: true, // Auto-generated
});

export const insertJobSettingsSchema = jobSettingsSchema.omit({
  id: true,
  lastUpdated: true,
  updatedBy: true,
});

// Referral Settings singleton document schema
export const referralSettingsSchema = z.object({
  id: z.literal("global"), // Fixed string "global" (singleton document)
  cvUploadBonusAmount: z.number().default(500),
  clientReferralBonusAmount: z.number().default(20000),
  noticePeriodDays: z.number().default(90),
  lastUpdated: z.date(),
  updatedBy: z.string(), // admin userId
});

export const insertReferralSettingsSchema = referralSettingsSchema.omit({
  id: true,
  lastUpdated: true,
  updatedBy: true,
});

// Types
export type User = z.infer<typeof userSchema>;
export type InsertUser = z.infer<typeof insertUserSchema>;

export type Candidate = z.infer<typeof candidateSchema>;
export type InsertCandidate = z.infer<typeof insertCandidateSchema>;

export type Resume = z.infer<typeof resumeSchema>;
export type AnonymizedProfile = z.infer<typeof anonymizedProfileSchema>;

export type Company = z.infer<typeof companySchema>;
export type InsertCompany = z.infer<typeof insertCompanySchema>;

export type Recruiter = z.infer<typeof recruiterSchema>;

export type Job = z.infer<typeof jobSchema>;
export type InsertJob = z.infer<typeof insertJobSchema>;

export type Referral = z.infer<typeof referralSchema>;
export type InsertReferral = z.infer<typeof insertReferralSchema>;

export type SkillsAnalysis = z.infer<typeof skillsAnalysisSchema>;
export type ExperienceAnalysis = z.infer<typeof experienceAnalysisSchema>;
export type AdminReferral = z.infer<typeof adminReferralSchema>;
export type InsertAdminReferral = z.infer<typeof insertAdminReferralSchema>;

export type MatchAnalysisResult = z.infer<typeof matchAnalysisResultSchema>;
export type MatchError = z.infer<typeof matchErrorSchema>;
export type AdminCandidate = z.infer<typeof adminCandidateSchema>;
export type InsertAdminCandidate = z.infer<typeof insertAdminCandidateSchema>;

export type StatusHistory = z.infer<typeof statusHistorySchema>;
export type RecruiterNote = z.infer<typeof recruiterNoteSchema>;

export type JobSettings = z.infer<typeof jobSettingsSchema>;
export type InsertJobSettings = z.infer<typeof insertJobSettingsSchema>;

export type ReferralSettings = z.infer<typeof referralSettingsSchema>;
export type InsertReferralSettings = z.infer<typeof insertReferralSettingsSchema>;

// Extended types for API responses
export type ReferralWithCandidate = Referral & {
  candidate: Candidate;
};

export type ReferralWithJob = Referral & {
  job: Job;
};

export type ReferralWithJobAndCandidate = Referral & {
  job: Job;
  candidate: Candidate;
};

export type UserStats = {
  totalReferrals: number;
  successfulHires: number;
  pendingReferrals: number;
  totalEarnings: number;
};

export type RecruiterStats = {
  activeJobs: number;
  totalReferrals: number;
  successfulHires: number;
  conversionRate: number;
};

export type AdminStats = {
  totalUsers: number;
  activeCandidates: number;
  totalJobs: number;
  totalReferrals: number;
  pendingRewards: number;
  paidRewards: number;
  totalRewardsAccumulated: number;
};

export type LeaderboardUser = {
  id: string;
  name: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  totalReferrals: number;
  successfulHires: number;
  totalEarnings: number;
  avatar?: string;
};

// UI-specific types that only contain data users should see
export type ReferralUIData = {
  id: string;
  candidateName: string;
  candidateEmail: string;
  jobTitle: string;
  jobId: string;
  // companyName removed - users should not see company information
  jobCategory?: string;
  jobLocation?: string;
  jobRemoteType?: string;
  jobDescription?: string;
  jobEmploymentType?: string;
  jobSkills?: string[]; // job skills for display
  status: ReferralStatus;
  rewardStatus: RewardStatus;
  rewardAmount?: number;
  referralDate: string; // formatted date string
  referralNote?: string;
  relationship?: string;
  candidateAvatar?: string;
  reward?: string; // formatted reward string like "500"
  bonusPoints?: boolean;
  rejectionNotes?: string; // Feedback from recruiter when rejected
};

// Client referral note schema
export const clientReferralNoteSchema = z.object({
  note: z.string(),
  date: z.date(),
  addedBy: z.string(), // userId of the person who added the note
});

// Client referral schema for referring companies to the platform
export const clientReferralSchema = z.object({
  id: z.string(),
  companyName: z.string(),
  pointOfContact: z.string(),
  website: z.string().optional(), // Original URL as provided by user
  normalizedWebsite: z.string().optional(), // Normalized URL for duplicate detection
  email: z.string().email(), // validated email format
  phoneNumber: z.string().optional(),
  referredBy: z.string(), // userId of the referrer
  status: z.enum(['pending', 'contacted', 'meeting_scheduled', 'converted', 'rejected']).default('pending'),
  rewardAmount: z.number().default(5000), // Default reward amount
  notes: z.array(clientReferralNoteSchema).default([]), // Client referral notes
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Insert schema for client referrals (without id, createdAt, updatedAt)
export const insertClientReferralSchema = clientReferralSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  status: true, // Auto-set to pending
  normalizedWebsite: true, // Auto-generated by backend for duplicate detection
});

// Types
export type ClientReferralNote = z.infer<typeof clientReferralNoteSchema>;
export type ClientReferral = z.infer<typeof clientReferralSchema>;
export type InsertClientReferral = z.infer<typeof insertClientReferralSchema>;

// Firebase function schemas
export const candidateHiredSchema = z.object({
  referralId: z.string().min(1, {message: "Referral ID is required."}),
  hiredDate: z.string().min(1, {message: "Hired date is required."}),
  agreedSalary: z.string().min(1, {message: "Agreed salary is required."}),
});

// Utility function types for email normalization
export const normalizeEmail = (email: string): string => email.toLowerCase().trim();
